import React, { useState } from 'react';
import { Account } from '../../../models';
import { Button, HStack, Text, VStack } from '@adc/parallax-component-library';
import mediator from '../../../utilities/mediator';
import i18n from '../../../utilities/i18n';
import { UserStatus as AccountStatus } from 'adc-sirius-module-common';
import MessageBanner from '../../messageBanner/MessageBanner';
import { Dialog } from 'tamagui';
import showUnderDevelopmentWarning from '../../../utilities/underDevelopmentWarning';

type Action =
  | 'resetPassword'
  | 'sendInvite'
  | 'resendInvite'
  | 'lock'
  | 'unlock';

type AccountActionButtonGroupProps = {
  account: Account;
  onSuccess: (account: Account) => void;
};

type ActionButtonWithModalProps = {
  action: Action;
  account: Account;
  onSuccess: (account: Account) => void;
};

const ActionButtonWithModal = ({
  action,
  account,
  onSuccess,
}: ActionButtonWithModalProps) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const topic =
    action === 'resendInvite' ? 'account:sendInvite' : `account:${action}`;

  const onPress = () => {
    if (!window.enableIncompleteFeatures) {
      showUnderDevelopmentWarning();
    }
  };

  const button = (
    <Button
      size="small"
      variant="tertiary"
      label={i18n.t(`Page_Accounts.action.${action}.label`)}
      onPress={onPress}
      singleLine
      testID={action}
    />
  );

  // TODO: Remove flag after CSP-1676 and CSP-2189 are complete
  if (window.enableIncompleteFeatures) {
    return (
      <Dialog open={open}>
        <Dialog.Trigger asChild onPress={() => setOpen(true)}>
          {button}
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay key="overlay" />
          <Dialog.Content key="content">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t(`Page_Accounts.action.${action}.label`)}
              </Text>
            </Dialog.Title>
            <VStack gap="$2" paddingVertical="$4">
              {error ? (
                <MessageBanner
                  type="danger"
                  bodyText={i18n.t('Common.errorGeneric')}
                  testID="error-banner"
                />
              ) : null}

              <Text>
                {i18n.t(`Page_Accounts.action.${action}.prompt`, {
                  email: account.email,
                })}
              </Text>
            </VStack>

            <HStack justifyContent="flex-end" gap="$2">
              <Button
                variant="secondary"
                label={i18n.t('Common.cancel')}
                aria-label={i18n.t('Common.cancel')}
                testID="cancel"
                onPress={() => {
                  setError(false);
                  setOpen(false);
                }}
              />
              <Button
                variant="primary"
                onPress={() => {
                  setLoading(true);
                  mediator
                    .request<Account>(topic, { key: account.key })
                    .then((account) => {
                      onSuccess(account);
                      setOpen(false);
                    })
                    .catch(() => setError(true))
                    .finally(() => setLoading(false));
                }}
                label={i18n.t('Common.ok')}
                testID="confirm"
                disabled={loading}
              />
            </HStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    );
  } else {
    return button;
  }
};

export const AccountActionButtonGroup = ({
  account,
  onSuccess,
}: AccountActionButtonGroupProps) => {
  const actions: Action[] = [];

  if (account.status !== AccountStatus.Unknown) {
    if (
      account.status !== AccountStatus.Locked &&
      account.status !== AccountStatus.NotInvited
    ) {
      actions.push('resetPassword');
    }

    if (account.status === AccountStatus.NotInvited) {
      actions.push('sendInvite');
    }

    if (
      account.status !== AccountStatus.Verified &&
      account.status !== AccountStatus.Locked &&
      account.status !== AccountStatus.NotInvited
    ) {
      actions.push('resendInvite');
    }

    if (account.status === AccountStatus.Verified) {
      actions.push('lock');
    }

    if (account.status === AccountStatus.Locked) {
      actions.push('unlock');
    }
  }

  return (
    <HStack gap="$1" testID={`${account.key}-action-button-group`}>
      {actions.map((action) => (
        <ActionButtonWithModal
          key={`${account.key}-${action}`}
          action={action}
          onSuccess={onSuccess}
          account={account}
        />
      ))}
    </HStack>
  );
};
